import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import '../styles/LoginSection.css';

/**
 * LoginSection - Microsoft authentication login component
 */
export const LoginSection: React.FC = () => {
  const { login, error, isLoggingIn, clearError, authStatus, errorReason, adminConsentUrl } = useAuth();
  const [localError, setLocalError] = useState<string | null>(null);

  const handleLogin = async () => {
    setLocalError(null);
    clearError();

    try {
      await login();
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  const handleAdminConsent = () => {
    if (adminConsentUrl) {
      window.open(adminConsentUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Check if user is authenticated but inactive
  const isInactive = authStatus?.authenticated && authStatus?.user?.isactive === false;

  const displayError = error || localError;

  const formatErrorMessage = (error: string): { title: string; message: string } => {
    if (error.includes('AADSTS') || error.includes('Azure') || error.includes('Microsoft')) {
      return {
        title: 'Erreur d\'authentification Microsoft',
        message: 'Une erreur s\'est produite lors de la connexion avec Microsoft. Veuillez reessayer.'
      };
    }
    if (error.toLowerCase().includes('network') || error.toLowerCase().includes('connexion')) {
      return {
        title: 'Erreur de connexion',
        message: 'Impossible de se connecter au serveur. Verifiez votre connexion internet.'
      };
    }
    if (error.toLowerCase().includes('cancelled') || error.toLowerCase().includes('annule')) {
      return {
        title: 'Connexion annulee',
        message: 'La connexion a ete annulee. Cliquez sur le bouton ci-dessus pour reessayer.'
      };
    }
    return {
      title: 'Authentification non disponible',
      message: error
    };
  };

  const formattedError = displayError ? formatErrorMessage(displayError) : null;

  return (
    <div className="login-section">
      <div className="login-box">
        <h1 className="login-title">Corpus</h1>
        <p className="login-tagline">l'IA souveraine pour le droit des affaires</p>

        <div className="login-actions">
          <button
            className="login-button"
            onClick={handleLogin}
            disabled={isLoggingIn}
            aria-label="Se connecter avec Microsoft"
          >
            {isLoggingIn ? (
              <>
                <span className="login-spinner" aria-hidden="true"></span>
                <span>Connexion en cours...</span>
              </>
            ) : (
              <>
                <svg
                  className="login-button-icon"
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <rect x="1" y="1" width="9" height="9" fill="#F35325" />
                  <rect x="11" y="1" width="9" height="9" fill="#81BC06" />
                  <rect x="1" y="11" width="9" height="9" fill="#05A6F0" />
                  <rect x="11" y="11" width="9" height="9" fill="#FFBA08" />
                </svg>
                <span>Se connecter avec Microsoft</span>
              </>
            )}
          </button>
        </div>

        {isInactive ? (
          <div className="login-error" role="alert">
            <svg
              className="login-error-icon"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <div className="login-error-content">
              <p className="login-error-title">Compte inactif</p>
              <p className="login-error-message">
                Le compte <strong>{authStatus?.user?.email}</strong> existe mais n'est pas actif.
                {' '}
                <a
                  href="https://account.microsoft.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="login-error-link"
                >
                  Gérer mon compte
                </a>
                {' '}ou connectez-vous avec un autre compte Microsoft.
              </p>
            </div>
          </div>
        ) : formattedError ? (
          <div className="login-error" role="alert">
            <svg
              className="login-error-icon"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <div className="login-error-content">
              <p className="login-error-title">{formattedError.title}</p>
              <p className="login-error-message">{formattedError.message}</p>

              {errorReason === 'CONSENT_REQUIRED' && adminConsentUrl && (
                <div style={{ marginTop: '16px' }}>
                  <button
                    onClick={handleAdminConsent}
                    className="login-button"
                    style={{
                      width: 'auto',
                      padding: '8px 16px',
                      fontSize: '14px'
                    }}
                  >
                    Donner le consentement administrateur
                  </button>
                  <p style={{
                    fontSize: '13px',
                    color: '#6c757d',
                    marginTop: '8px',
                    lineHeight: '1.4'
                  }}>
                    Si vous etes administrateur de votre organisation, cliquez sur ce bouton pour autoriser l'application.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {!formattedError && !isInactive && (
          <div className="login-notice">
            <p className="login-notice-text">
              Authentification Microsoft requise pour acceder a l'application.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
